import React, { useState, useMemo, useCallback, useRef } from 'react';
import { connectSortBy } from 'react-instantsearch-core';
import {
    StyledGroupTitle,
    StyledOrderContainer,
    StyledOrderTrigger,
    StyledSortOption,
    StyledSortList,
    StyledListItem,
    StyledDrawerContainer,
    StyledDrawerOverlay,
    StyledDrawerContent,
    StyledDrawerHeader,
    StyleContent,
    StyledCloseButton,
    StyledSortDrawerItem,
    StyledSortDrawerOption,
} from './styled';
import { useTranslation } from '~/shared/utils/translation';
import { TranslationKey } from '~/lib/data-contract';
import ChevronDown from '$icons/chevron-down.svg';
import ChevronUp from '$icons/chevron-up.svg';
import Close from '$icons/close.svg';
import { useFilterUIState } from '../../hooks/useFilterUIState';
import { useClickAway } from 'react-use';
import { useGtm } from '~/shared/utils';
import { invalidateCache } from '~/shared/utils/algolia';

interface SortType {
    key: string;
    value: string;
}

interface SortByProps {
    items: SortType[];
    currentRefinement: string;
    refine: (value?: string) => void;
}

const CustomSortBy = ({ items, currentRefinement, refine }: SortByProps) => {
    const { isSortDrawerOpen, setIsSortDrawerOpen } = useFilterUIState(
        ({ setIsSortDrawerOpen, isSortDrawerOpen }) => ({
            setIsSortDrawerOpen,
            isSortDrawerOpen,
        })
    );
    const [isOpen, setIsOpen] = useState(false);
    const { translate } = useTranslation();
    const { trackProductSorting } = useGtm();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { setIsMobileFilterDrawerOpen } = useFilterUIState();

    const currentLabel = useMemo(() => {
        return items?.find((item) => item.key === currentRefinement)?.value;
    }, [items, currentRefinement]);

    const setOrder = useCallback(
        (orderIndex: string) => {
            if (currentRefinement !== orderIndex) {
                const { pathname } = window.location;
                invalidateCache(pathname);
            }

            refine(orderIndex);
            setIsOpen(false);
            const newSortOrder = items.find((item) => item.key === orderIndex);
            trackProductSorting(translate(newSortOrder?.value as TranslationKey));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [refine, setIsOpen, items, trackProductSorting, translate]
    );

    useClickAway(
        dropdownRef,
        (e: MouseEvent) => {
            if (dropdownRef?.current?.contains(e.target as HTMLElement)) return;
            setIsOpen(false);
        },
        ['click']
    );

    if (!items.length) return null;

    const closeDrawer = () => {
        setIsSortDrawerOpen(false);
        setIsMobileFilterDrawerOpen(false);
    };

    return (
        <StyledOrderContainer ref={dropdownRef}>
            <StyledOrderTrigger isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
                <StyledGroupTitle>{translate(currentLabel as TranslationKey)}</StyledGroupTitle>
                {!isOpen ? <ChevronDown aria-hidden="true" /> : <ChevronUp aria-hidden="true" />}
            </StyledOrderTrigger>
            {isOpen ? (
                <StyledSortList>
                    {items.map((item) => (
                        <StyledListItem key={item.key} onClick={() => setOrder(item.key)}>
                            <StyledSortOption isSelected={currentRefinement === item.key}>
                                {translate(item.value as TranslationKey)}
                            </StyledSortOption>
                        </StyledListItem>
                    ))}
                </StyledSortList>
            ) : null}
            <StyledDrawerContainer>
                <StyledDrawerOverlay
                    isOpen={isSortDrawerOpen}
                    onClick={closeDrawer}
                ></StyledDrawerOverlay>
                <StyledDrawerContent isOpen={isSortDrawerOpen} onClick={closeDrawer}>
                    <StyledDrawerHeader>
                        {translate('commerce.sort.sort')}
                        <StyledCloseButton shape={'icon'}>
                            <Close aria-hidden="true" />
                        </StyledCloseButton>
                    </StyledDrawerHeader>
                    <StyleContent>
                        {items.map((item) => (
                            <StyledSortDrawerItem key={item.key} onClick={() => setOrder(item.key)}>
                                <StyledSortDrawerOption isSelected={currentRefinement === item.key}>
                                    {translate(item.value as TranslationKey)}
                                </StyledSortDrawerOption>
                            </StyledSortDrawerItem>
                        ))}
                    </StyleContent>
                </StyledDrawerContent>
            </StyledDrawerContainer>
        </StyledOrderContainer>
    );
};

export const SortBy = React.memo(connectSortBy(CustomSortBy));
